'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { userPrefersReducedMotion } from '../../lib/utils/prefersReducedMotion';
import { ResponsiveImage } from '../Image/ResponsiveImage';
const ANIMATION_OPTIONS = {
    duration: 800,
    easing: 'ease-in-out',
    fill: 'forwards',
};
export const CrossFadeImage = ({ src, loading = 'default', className, onLoad, ...rest }) => {
    const [srcs, setSrcs] = useState([src, src]);
    const activeLayerRef = useRef(0);
    const currentSrcRef = useRef(src);
    const pendingRef = useRef(null);
    const firstImgSlotRef = useRef(null);
    const secondImgSlotRef = useRef(null);
    const animationFrameRef = useRef(null);
    useLayoutEffect(() => {
        if (src === currentSrcRef.current)
            return;
        currentSrcRef.current = src;
        const nextActive = activeLayerRef.current === 0 ? 1 : 0;
        pendingRef.current = { layer: nextActive, src };
        const incomingImgEl = nextActive === 0 ? firstImgSlotRef.current : secondImgSlotRef.current;
        if (incomingImgEl)
            incomingImgEl.style.opacity = '0';
        setSrcs(prev => {
            const next = [...prev];
            next[nextActive] = src;
            return next;
        });
        const incomingImg = incomingImgEl?.querySelector('img');
        if (incomingImg?.complete)
            incomingImg.dispatchEvent(new Event('load'));
        return () => {
            if (animationFrameRef.current !== null)
                cancelAnimationFrame(animationFrameRef.current);
        };
    }, [src]);
    const handleLayerLoad = (layerIndex, layerSrc) => (e) => {
        const pending = pendingRef.current;
        if (!pending || pending.layer !== layerIndex || pending.src !== layerSrc)
            return;
        pendingRef.current = null;
        onLoad?.(e);
        const incomingImgEl = layerIndex === 0 ? firstImgSlotRef.current : secondImgSlotRef.current;
        const outgoingImgEl = layerIndex === 0 ? secondImgSlotRef.current : firstImgSlotRef.current;
        activeLayerRef.current = layerIndex;
        animationFrameRef.current = requestAnimationFrame(() => {
            outgoingImgEl?.getAnimations().forEach(a => a.cancel());
            incomingImgEl?.getAnimations().forEach(a => a.cancel());
            if (incomingImgEl)
                incomingImgEl.style.zIndex = '1';
            if (outgoingImgEl)
                outgoingImgEl.style.zIndex = '0';
            if (userPrefersReducedMotion) {
                if (outgoingImgEl)
                    outgoingImgEl.style.opacity = '0';
                if (incomingImgEl)
                    incomingImgEl.style.opacity = '1';
            }
            else {
                outgoingImgEl?.animate([{ opacity: 1 }, { opacity: 0 }], ANIMATION_OPTIONS);
                incomingImgEl?.animate([{ opacity: 0 }, { opacity: 1 }], ANIMATION_OPTIONS);
            }
        });
    };
    return (_jsxs(Wrapper, { className: className, children: [_jsx(Layer, { ref: firstImgSlotRef, children: _jsx(LayerImage, { ...rest, loading: loading, src: srcs[0], onLoad: handleLayerLoad(0, srcs[0]) }) }), _jsx(Layer, { ref: secondImgSlotRef, children: _jsx(LayerImage, { ...rest, loading: loading, src: srcs[1], onLoad: handleLayerLoad(1, srcs[1]) }) })] }));
};
const Wrapper = styled.div `
  position: relative;
  width: 100%;
  height: 100%;
  isolation: isolate;
`;
const Layer = styled.div `
  position: absolute;
  inset: 0;
`;
const LayerImage = styled(ResponsiveImage) `
  display: block;
  width: 100%;
  height: 100%;
`;
//# sourceMappingURL=index.js.map